/**
 * Module-level singleton state for the right-side chat panel (PLT-4625).
 *
 * Why a module-level pub-sub instead of React context: the panel is opened
 * from places that may live deep inside the component tree (e.g. an SVG
 * `<g>` inside a Net Impact `DimensionBar`, eventually other contextual
 * entry points), and we want those call sites to be able to call a plain
 * function rather than first wiring a `useChatPanel()` hook through
 * intermediate components.
 *
 * The panel itself subscribes to this state via `useSyncExternalStore` and
 * re-renders only when something actually changes. Components that don't
 * render the panel never read this state, so the singleton has zero cost
 * elsewhere.
 *
 * Lifecycle:
 *   - `openChatPanel({ prompt?, entityId?, entityType?, extra?, source? })` —
 *     sets a pending seed; the panel mounts and consumes the seed exactly once.
 *     Always resets the underlying assistant-ui thread, so every way into the
 *     panel (a suggested question, the `Ask AI` link, "Continue in chat")
 *     opens a fresh conversation rather than whatever the user was looking at
 *     before (PLT-7880). Earlier conversations stay in chat history.
 *   - `closeChatPanel()` — hides the panel. The thread state stays on the
 *     still-mounted root-level runtime, but the next open resets it.
 *   - "Expand to full chat" — `closeChatPanel()` + `navigate('/chat')`
 *     (without `?prompt=`). The `/chat` route mounts, sees no prompt,
 *     leaves the runtime alone, and renders the same thread the panel was
 *     showing. This is the primary motivation for lifting the runtime to
 *     the app root.
 */
import type { ChatProvider } from '@upright/common/chat/provider-capabilities';

/** Allowed values for analytics tagging. Mirrors `composerState.pendingSource`. */
export type ChatPanelMessageSource =
  | 'user_input'
  | 'welcome_suggestion'
  | 'followup_suggestion'
  | 'campaign_link'
  | 'dimension_bar';

/**
 * The entity a seed is about (`"entity:<type>:<id>"`), or `'global'` when it
 * has no entity binding. Part of the double-click guard's signature.
 */
function originKeyForSeed(seed: Pick<OpenChatPanelOptions, 'entityId' | 'entityType'>): string {
  if (seed.entityId !== undefined && seed.entityType !== undefined) {
    return `entity:${seed.entityType}:${seed.entityId}`;
  }
  return 'global';
}

/**
 * Clear the double-click guard. Called on `New chat`: the next click on the
 * bar the user came from is then a legitimate fresh open, not an accidental
 * double-click, and would otherwise see no reaction for up to 800ms.
 */
export function resetChatPanelDoubleClickGuard(): void {
  _lastSeedSignature = null;
  _lastSeedAtMs = 0;
}

export interface OpenChatPanelOptions {
  /**
   * Visible prompt that will be auto-submitted when the panel opens.
   *
   * Optional (PLT-4665) — call sites that just want to open the panel with
   * an entity / page-context binding pre-applied (e.g. the top-bar `Ask AI`
   * link on an entity page) can pass everything except `prompt`, in which
   * case the panel mounts with the binding in place but waits for the user
   * to type their own question rather than auto-submitting.
   */
  prompt?: string;
  /**
   * Optional entity binding — paired with `entityType`. When set, the chat
   * adapter prepends a hidden context line so the model knows which model
   * entity the question is about, while the user only sees the visible
   * prompt in their bubble. Same mechanism used by the `/chat?entityId=…`
   * deep-link.
   */
  entityId?: string;
  entityType?: string;
  /**
   * Optional extra hidden-context key/value pairs (PLT-4676). Merged into the
   * same `[Context: …]` line the chat adapter prepends to the first user
   * message, after the entity binding. Used by panel openers that want to
   * pin the conversation to a specific data row (e.g. the R/O table's
   * "Modelling details" icon pins it to a particular risk/opportunity
   * driver-pair via `matterId`, `valence`, `driver`, …, `intent`,
   * `source`). Keys are emitted in insertion order, values verbatim — they
   * originate in code, not user input.
   *
   * Carried into `hiddenEntityContext.value.extra` by the panel's
   * seed-consumption effect, so the chat adapter picks it up on every send
   * (the runtime replays the full history each turn).
   */
  extra?: Record<string, string>;
  /**
   * Source tag for analytics. Defaults to `dimension_bar` since that is
   * the only entry-point in v1 of PLT-4625, but kept open for future
   * panel openers (e.g. clicking a financial-effects row, a DMA matter).
   */
  source?: ChatPanelMessageSource;
  /**
   * Visible page-context chip displayed above the thread — shows the
   * user (and the model) which page they were on when they opened the
   * chat. The label is rendered as a small grey chip with an Upright
   * logo, similar in styling to citation chips. The URL is also folded
   * into the hidden context line sent to the model on every turn, so
   * the model knows the source page for follow-ups too. Cleared on
   * `New chat`.
   */
  pageContext?: { label: string; href: string; entityType?: string; lensLabel?: string };
  /**
   * The opener's web search choice, applied to the chat the seed lands in
   * (PLT-7271). Only a launcher on a provider whose web search is off at the
   * start of each chat passes one; see `useLauncherWebSearchToggle`.
   */
  webSearch?: WebSearchHandOff;
}

/**
 * A launcher's web search choice for the chat it opens (PLT-7271). Defined
 * here rather than in `upright-chat-shared.ts`, which imports this module.
 */
export interface WebSearchHandOff {
  /** The provider the choice was made for. */
  provider: ChatProvider;
  enabled: boolean;
}

interface ChatPanelState {
  open: boolean;
  /**
   * The seed waiting to be consumed by the panel on mount. `null` once
   * the panel has consumed it (see `consumeChatPanelSeed`), so a re-render
   * never applies the same seed twice.
   */
  pendingSeed: OpenChatPanelOptions | null;
  /**
   * Monotonically increasing counter — incremented on every
   * `openChatPanel(seed)` call. The side panel uses it as a
   * React `key` on its inner body so each new conversation forces a
   * clean unmount/mount cycle of `ChatSidePanelOpen`, eliminating any
   * chance of stale effect closures or runtime state leaking from a
   * previous seed.
   *
   * Why this is needed: PLT-4625 follow-up surfaced a regression where
   * the flow `bar click → expand to full chat → browser-back → bar
   * click` opened the panel in the welcome state instead of the seeded
   * conversation. The root cause was the seed-consumption effect not
   * re-running reliably across the panel-already-mounted vs
   * panel-just-mounted distinction. Re-mounting on every seed makes
   * the behaviour identical to a fresh first-time open.
   *
   * `closeChatPanel()` does not bump the counter.
   */
  seedNonce: number;
}

let _state: ChatPanelState = { open: false, pendingSeed: null, seedNonce: 0 };
const _listeners = new Set<() => void>();

/**
 * Tracks the signature + timestamp of the last `openChatPanel` call so
 * an immediate duplicate (typically a double-click on the same Net
 * Impact bar — PLT-4625 follow-up issue 3) is dropped instead of
 * opening a second chat for the same question.
 *
 * The bug surfaced as "clicking a bar twice runs two chats
 * simultaneously": the second click lands while the first run is
 * still streaming, the user sees two parallel chats, and the cancel
 * button only kills one of them.
 *
 * We dedupe purely on `originKey + prompt` because that's the part
 * the user controls — same bar in the same page yields the same
 * signature. The `entityType`/`entityId`/`source` triplet is already
 * folded into `originKey`, so a single field is sufficient.
 *
 * 800ms is long enough to swallow a fast double-click (or a touch
 * device's "tap, tap" with brief contact debounce) without
 * suppressing a legitimate second click made after the user
 * deliberately read the previous answer. We don't gate on the
 * runtime's `isRunning` state because (a) we'd need to drag a
 * runtime reference into this plain-state module, and (b) a fast
 * second click can land before the first run flips the flag to
 * `true`, so a timestamp-based debounce is more robust than a
 * state-based one for the immediate-double-click failure mode.
 */
const DOUBLE_CLICK_DEDUP_MS = 800;
let _lastSeedSignature: string | null = null;
let _lastSeedAtMs = 0;

function notify(): void {
  for (const l of _listeners) l();
}

/**
 * Snapshot accessor for `useSyncExternalStore`. Reference-stable between
 * mutations so React only re-renders the panel when something actually
 * changed.
 */
export function getChatPanelState(): ChatPanelState {
  return _state;
}

export function subscribeChatPanelState(listener: () => void): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

/**
 * Open the panel on a new conversation, optionally seeded with a prompt.
 *
 * Every open resets the thread and remounts the panel (PLT-7880): in
 * investor demos a panel reopening on the previous conversation looks
 * stale. The previous conversation stays in chat history. A seed with a
 * `prompt` lands as the first message; a prompt-less one (the `Ask AI`
 * link) applies its entity binding and page chip and waits for the user
 * to type. Only "Expand to full view" carries a conversation forward, and
 * it does so without going through here.
 *
 * `openChatPanel` is a pure state mutation — safe to call from any
 * context (event handler, effect, etc.). The panel itself handles the
 * thread reset + auto-send on mount (see `chat-side-panel.tsx`).
 */
export function openChatPanel(options: OpenChatPanelOptions): void {
  const newOriginKey = originKeyForSeed(options);
  // Drop accidental immediate duplicates (PLT-4625 follow-up issue 3).
  // See the `DOUBLE_CLICK_DEDUP_MS` block above for the rationale —
  // signature is `origin|prompt` so a second click on the same bar
  // within the window is a no-op, but a different prompt against the
  // same entity (e.g. clicking a different bar of the same Neste
  // profile) still goes through. `prompt` may be `undefined` for
  // bind-only opens (PLT-4665 top-bar `Ask AI` on entity pages); we
  // coerce to a stable sentinel so two rapid bind-only clicks on the
  // same entity dedup just like two rapid bar clicks would.
  const signature = `${newOriginKey}|${options.prompt ?? '<no-prompt>'}`;
  const now = Date.now();
  if (signature === _lastSeedSignature && now - _lastSeedAtMs < DOUBLE_CLICK_DEDUP_MS) {
    return;
  }
  _lastSeedSignature = signature;
  _lastSeedAtMs = now;
  _state = {
    open: true,
    pendingSeed: { ...options },
    // A clean remount so the seed-consumption effect runs against a fresh
    // component tree. See `ChatPanelState.seedNonce` above.
    seedNonce: _state.seedNonce + 1,
  };
  notify();
}

export function closeChatPanel(): void {
  if (!_state.open) return;
  // Preserve `seedNonce` — only `openChatPanel` bumps it.
  _state = { open: false, pendingSeed: null, seedNonce: _state.seedNonce };
  // Clear the double-click dedup state on explicit close. The
  // dedup window only exists to swallow an accidental second click
  // on a still-streaming run; once the user has actively dismissed
  // the panel, a fresh re-open of the same bar is a legitimate
  // user intent that shouldn't be dropped just because it falls
  // within the 800ms window. (See `_lastSeedSignature` docstring.)
  _lastSeedSignature = null;
  _lastSeedAtMs = 0;
  notify();
}

/**
 * Called by the panel after it consumes the pending seed (resets thread,
 * sets entity context, appends the prompt). Returns the consumed seed for
 * the caller to act on, and clears it from the store so a re-render won't
 * fire the seed twice.
 */
export function consumeChatPanelSeed(): OpenChatPanelOptions | null {
  const seed = _state.pendingSeed;
  if (seed === null) return null;
  _state = { ..._state, pendingSeed: null };
  // No notify() — clearing the seed is an internal bookkeeping update.
  // The panel already re-rendered for `open: true`; firing another
  // re-render here would just churn. (The `seedNonce` is intentionally
  // unchanged: the consumer has already keyed off it, the seed is now
  // null so a re-render would no-op anyway, and bumping it here would
  // double-bump on next `openChatPanel` and trigger a spurious remount.)
  return seed;
}
